// r24 — AU city × NAATI delivery page (unique caseworker note per city)
import { Link } from "@tanstack/react-router";
import HubLeadCTA from "@/components/HubLeadCTA";
import NaatiAuthorByline from "@/components/NaatiAuthorByline";
import NaatiTripleKeyLinks from "@/components/NaatiTripleKeyLinks";
import NaatiSchemaGraph from "@/components/NaatiSchemaGraph";
import type { AuCity } from "@/data/naati-au-cities";
import { NAATI_AU_CITIES } from "@/data/naati-au-cities";

// EN pages must never print Thai units (locale-purity rule).
function etaEn(eta: string): string {
  return eta.replace(/วันทำการ/g, "business days").replace(/วัน/g, "days").trim();
}

export interface NaatiAuCityRendererProps {
  locale: "th" | "en";
  city: AuCity;
}

export default function NaatiAuCityRenderer({ locale, city }: NaatiAuCityRendererProps) {
  const isTh = locale === "th";
  const home = isTh ? "/translate/naati" : "/en/translate/naati";
  const hub = isTh ? "/translate/naati/au" : "/en/translate/naati/au";
  const cityName = isTh ? city.nameTh : city.nameEn;
  const note = isTh ? city.caseworkerNoteTh : city.caseworkerNoteEn;
  const others = NAATI_AU_CITIES.filter(
    (c) => c.state === city.state && c.slug !== city.slug,
  ).slice(0, 4);

  const path = isTh ? `/translate/naati/au/${city.slug}` : `/en/translate/naati/au/${city.slug}`;
  const title = isTh
    ? `แปล NAATI ส่ง ${cityName} — DHL ${city.postageEtaFromBkk}`
    : `NAATI Translation shipped to ${cityName} — DHL ${etaEn(city.postageEtaFromBkk)}`;
  const desc = isTh
    ? `บริการแปลเอกสาร NAATI Certified ส่งตรงถึง ${cityName} รัฐ ${city.stateEn} — DHL Express ${city.postageEtaFromBkk} ราคาเริ่ม ฿1,300/หน้า`
    : `NAATI Certified translation delivery to ${cityName}, ${city.stateEn} — DHL Express ${city.postageEtaFromBkk}, from THB 1,300/page.`;

  return (
    <main className="mx-auto max-w-4xl px-5 py-12 sm:px-8 sm:py-16">
      <NaatiSchemaGraph
        locale={locale}
        path={path}
        title={title}
        description={desc}
        areaName={city.nameEn}
        areaRegion={city.stateEn}
        areaCountry="AU"
        crumbs={[
          { name: isTh ? "หน้าแรก" : "Home", path: isTh ? "/" : "/en" },
          { name: "NAATI", path: home },
          { name: "Australia", path: hub },
          { name: cityName, path },
        ]}
      />
      <nav className="mb-6 text-xs text-slate-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-amber-700">{isTh ? "หน้าแรก" : "Home"}</Link>
        <span className="mx-2">/</span>
        <Link to={home as any} className="hover:text-amber-700">NAATI</Link>
        <span className="mx-2">/</span>
        <Link to={hub as any} className="hover:text-amber-700">Australia</Link>
      </nav>

      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700" data-speakable="true">
        {isTh ? `แปล NAATI ส่ง ${city.stateEn}` : `NAATI Translation delivery to ${city.stateEn}`}
      </div>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl" data-speakable="true">
        {isTh
          ? `แปล NAATI ส่ง ${cityName} — DHL ${city.postageEtaFromBkk}`
          : `NAATI Translation shipped to ${cityName} — DHL ${etaEn(city.postageEtaFromBkk)}`}
      </h1>

      <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
          {isTh ? "โน้ตจากทีมเคส" : "Field note"}
        </div>
        <p className="mt-2 text-base font-medium text-navy-deep direct-answer" data-speakable="true">
          {note}
        </p>
      </section>

      <section className="mt-10 grid gap-4 sm:grid-cols-2">
        <Card label={isTh ? "รัฐ" : "State"} value={`${city.stateEn} (${city.state})`} />
        <Card label={isTh ? "เขตเวลา" : "Timezone"} value={city.timezone} />
        <Card label={isTh ? "สนง. BDM" : "State BDM"} value={city.bdmOffice} />
        {city.dhaOffice && <Card label={isTh ? "สนง. DHA ใกล้ที่สุด" : "Nearest DHA"} value={city.dhaOffice} />}
        <Card label={isTh ? "ชุมชนไทย" : "Thai community"} value={city.thaiCommunity} />
        <Card label="DHL ETA (BKK→)" value={city.postageEtaFromBkk} />
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? `วีซ่าที่ยื่นบ่อยจาก ${cityName}` : `Visas commonly lodged from ${cityName}`}
        </h2>
        <ul className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
          {city.popularVisas.map((v) => (
            <li key={v} className="flex gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? "เอกสารที่สั่งบ่อย" : "Most-ordered documents"}
        </h2>
        <ul className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
          {city.popularDocs.map((d) => (
            <li key={d}>
              <Link
                to={(isTh ? `/translate/naati/doctype/${d}` : `/en/translate/naati/doctype/${d}`) as any}
                className="text-amber-700 hover:underline"
              >
                {d.replace(/-/g, " ")}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {others.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">
            {isTh ? `เมืองอื่นๆ ใน ${city.stateEn}` : `Other cities in ${city.stateEn}`}
          </h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {others.map((o) => (
              <li key={o.slug}>
                <Link
                  to={(isTh ? `/translate/naati/au/${o.slug}` : `/en/translate/naati/au/${o.slug}`) as any}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700 hover:border-amber-400 hover:text-amber-700"
                >
                  {isTh ? o.nameTh : o.nameEn}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <NaatiAuthorByline lang={isTh ? "th" : "en"} />

      <NaatiTripleKeyLinks
        mode={isTh ? "th" : "en"}
        countryNameTh="ออสเตรเลีย"
        countryNameEn="Australia"
      />

      <HubLeadCTA
        locale={isTh ? "th" : "en"}
        service="NAATI Translation"
        title={isTh ? `พร้อมส่งแปล NAATI ไป ${cityName}?` : `Ready to ship a NAATI translation to ${cityName}?`}
      />
    </main>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-800">{value}</div>
    </div>
  );
}
